import * as React from 'react';
import styled, { css } from 'styled-components';
import { colors } from './colors/color';
import { Icon, iconName } from './icons/Icons';
import { TextAvatar } from './avatar/TextAvatar';

export type TabberVariant = 'default' | 'slim' | 'text' | 'text-minimal';
export type TabberAlignment = 'left' | 'center' | 'right';

export type TabItem = {
    tabName:string,
    leadingContent?:iconName,
    avatarCount?:number,
    text:string,
    disabled?:boolean,
    hidden?:boolean
}

type TabberProps = {
    variant?:TabberVariant,
    inline?:boolean,
    alignment?:TabberAlignment,
    selectedTabName?:string,
    items:TabItem[],
    style?:React.CSSProperties,
    onClick?:(tabName:string)=>void
}
export function Tabber(props:TabberProps):JSX.Element
{
    function handleClickTab(item:TabItem)
    {
        props.onClick?.(item.tabName);
    }

    const styleVariant = props.variant || 'default';
    const styleAlignment = props.alignment || 'left';

    return (
        <TabContainer variant={styleVariant} alignment={styleAlignment} style={props.style}>
            {props.items.filter(t => !t.hidden).map(t => 
                <Tab key={t.tabName} 
                    variant={styleVariant}
                    selected={t.tabName === props.selectedTabName} 
                    disabled={!!t.disabled}
                    inline={!!props.inline}
                    onClick={() => handleClickTab(t)}>

                    {t.leadingContent && 
                            <Icon name={t.leadingContent} />
                    }
                    <span style={{marginRight:t.avatarCount ? '4px' : '0'}}>
                        {t.text}
                    </span>
                    {t.avatarCount !== undefined &&
                        <TextAvatar
                            backgroundColor={colors.tabber.avatarBackground}
                            text={t.avatarCount.toString()}
                            size='20px'
                        />
                    }
                </Tab>
            )}
        </TabContainer>
    );
}

const TabContainer = styled.div<{variant:TabberVariant, alignment:TabberAlignment}>`
    display: flex;
    justify-content:stretch;
    align-items: center;
    padding: 4px;
    width: 100%;
    border: 1px solid ${colors.tabber.containerBorder};

    ${props => props.variant === 'default' && css`
        gap: 4px;
    `}

    ${props => props.variant === 'slim' && css`
        gap: 4px;
    `}

    ${props => props.variant === 'text' && css`
        justify-content: ${props.alignment === 'center'
            ? 'center'
            : props.alignment === 'right'
                ? 'flex-end'
                : 'flex-start'};
        border:none;
    `}

    ${props => props.variant === 'text-minimal' && css`
        justify-content: ${props.alignment === 'center'
            ? 'center'
            : props.alignment === 'right'
                ? 'flex-end'
                : 'flex-start'};
        border:none;
    `}

`;
const TabDefault = styled.button<{selected:boolean, disabled:boolean, inline:boolean}>`
    border:none;
    display:flex;
    flex-grow: 1;
    gap: 4px;
    align-items:center;
    justify-content: center;
    min-height: 34px; // Must be set since icons are slightly larger than avatars and would increase height otherwise for only some buttons
    cursor: ${props => !props.disabled ? 'pointer' : 'default'};
    border:none;
    border-radius: 0px;


    color:${props => props.disabled 
        ? colors.text.muted
        : colors.text.normal
    };

    svg {
        fill:${props => props.disabled 
            ? colors.text.muted
            : colors.text.normal};
    }
    
    font-weight: 300;
    line-height: 22px;
    letter-spacing: 0.5px;

    background-color: ${props => props.selected ? colors.tabber.selected.background : colors.tabber.default.background};
    padding: 12px 24px;
    font-size: 16px;

    &:hover {
        background-color: ${props => props.disabled ? colors.tabber.disabled.background : colors.tabber.hover.background};
    }

    flex-grow: ${props => props.inline ? 0 : 1};
`;

const TabSlim = styled(TabDefault)<{selected:boolean, disabled:boolean, inline:boolean}>`
    padding: 4px 20px;
    font-size: 14px;
`;




// TextVariants simply use the background color for the bottom border




const TabText = styled(TabDefault)<{selected:boolean, disabled:boolean, inline:boolean}>`
   
    background-color: ${colors.tabber.default.background};
    padding: 4px 20px;
    font-size: 14px;
    border-bottom: 2px solid ${props => props.selected ? colors.tabber.hover.background :  colors.tabber.default.background};

    &:hover, &:hover svg {
        background-color: ${colors.tabber.default.background};
        border-bottom-color: ${props => !props.disabled && colors.tabber.hover.background};
    }
`;
    

const TabTextMinimal = styled(TabText)<{selected:boolean, disabled:boolean, inline:boolean}>`

    padding: 4px 20px;

`;

type TabProps = {
    variant:TabberVariant,
    selected:boolean,
    disabled:boolean,
    inline:boolean,
    children:React.ReactNode,
    onClick:()=>void
}

function Tab(props:TabProps):JSX.Element
{
    const commonProps = {
        selected: props.selected,
        disabled: props.disabled,
        inline: props.inline,
        onClick: props.onClick,
    };

    switch (props.variant)
    {
        case 'slim':
            return <TabSlim {...commonProps}>{props.children}</TabSlim>;
        case 'text':
            return <TabText {...commonProps}>{props.children}</TabText>;
        case 'text-minimal':
            return <TabTextMinimal {...commonProps}>{props.children}</TabTextMinimal>;
        case 'default':
        default:
            return <TabDefault {...commonProps}>{props.children}</TabDefault>;
    }
}